import React, { useEffect, useState, useCallback } from 'react';
import { observer } from 'mobx-react';
import moment from 'moment';
import { Fetcher } from '@this/src/util';
import { Button } from '@this/components/shared/ui/inputs/button';
import { Loading } from '@this/shared/ui/feedbacks/loading';
import type { MessageLogArgs } from '@this/domain/arrangement/message_log';
import { MessageLog } from '@this/domain/arrangement/message_log';
import { Table, TableBody, TableCell, TableHead, TableRow } from '@this/components/shared/ui/data_displays/table';
import { Alert } from '@this/shared/ui/feedbacks/alert';

interface Props {
  traceId: number;
}

interface MessageLogResponse {
  logs: MessageLogArgs[];
}

export const OrderItemMessageLogs = observer(({ traceId }: Props) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [messageLogs, setMessageLogs] = useState<MessageLog[]>([]);
  const [messageLog, setMessageLog] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const fetchLogs = useCallback(() => {
    setIsLoading(true);
    Fetcher.get<MessageLogResponse>(`/arrangement/order_item/${traceId}/message_logs.json`)
      .then(result => setMessageLogs(result.logs.map((args: MessageLogArgs) => new MessageLog(args))))
      .finally(() => setIsLoading(false));
  }, [traceId]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const saveLog = useCallback(() => {
    if (!messageLog.trim()) return;

    const params = {
      message_log: messageLog,
      trace_id: traceId
    };
    Fetcher.put(`/arrangement/order_item/${traceId}/create_message_log.json`, params)
      .then(() => {
        setMessageLog('');
        fetchLogs();
      })
      .catch(() => {
        setErrorMessage('メッセージを保存できませんでした。');
      });
  }, [messageLog, traceId, fetchLogs]);

  return (
    <div style={{ padding: '8px' }}>
      <h3 style={{ marginBottom: '8px' }}>この商品に関するやり取り</h3>
      {errorMessage && (
        <Alert variant="danger" onClose={() => setErrorMessage(null)} style={{ marginBottom: '8px' }}>
          {errorMessage}
        </Alert>
      )}
      {isLoading ? (
        <Loading />
      ) : (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell nowrap>手配者名</TableCell>
              <TableCell nowrap>内容</TableCell>
              <TableCell nowrap>記載日時</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {messageLogs.map(log => (
              <TableRow key={log.createdAt}>
                <TableCell nowrap>{log.arrangerName}</TableCell>
                <TableCell style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{log.body}</TableCell>
                <TableCell nowrap>{moment(log.createdAt).format('YYYY-MM-DD HH:mm')}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
        <input
          type="text"
          placeholder="メッセージ"
          value={messageLog}
          onChange={e => setMessageLog(e.target.value)}
          style={{ flex: 1 }}
        />
        <Button onClick={saveLog} disabled={!messageLog.trim()}>
          送信
        </Button>
      </div>
    </div>
  );
});
